/**
 * V291.2: Single source of truth for verified UI state
 * Priority: entitlements > database flag (handles cache lag)
 */

import { Entitlements } from '@/services/entitlement/EntitlementService';

export interface VerificationStatus {
  isVerified: boolean;
  source: 'entitlements' | 'database' | 'none';
}

/**
 * Determine if user should see verified UI
 * @param entitlements - From EntitlementService (cached 30s, respects grace periods)
 * @param dbIsVerified - From users.is_verified (direct from DB)
 * @returns Unified verification status
 * 
 * Priority order:
 * 1. Entitlements (respects subscription status, grace periods)
 * 2. Database flag (handles manual admin changes, cache lag)
 * 
 * @example
 * // User with active subscription
 * getVerificationStatus({ isVerified: true }, false)
 * // Returns: { isVerified: true, source: 'entitlements' }
 * 
 * @example
 * // User with DB flag but no entitlements (cache lag)
 * getVerificationStatus({ isVerified: false }, true)
 * // Returns: { isVerified: true, source: 'database' }
 */
export function getVerificationStatus(
  entitlements: Entitlements | null | undefined,
  dbIsVerified: boolean | undefined | null
): VerificationStatus {
  // Priority 1: Entitlements (respects grace periods, subscription status)
  if (entitlements?.isVerified === true) {
    return { isVerified: true, source: 'entitlements' };
  }
  
  // Priority 2: Database flag (handles manual admin changes, cache lag)
  if (dbIsVerified === true) {
    return { isVerified: true, source: 'database' };
  }
  
  return { isVerified: false, source: 'none' };
}

/**
 * Simple boolean gate for UI components
 * Use this everywhere to determine if verified UI should be shown
 * 
 * @param entitlements - From EntitlementService
 * @param dbIsVerified - From users.is_verified
 * @returns true if user should see verified UI
 * 
 * @example
 * // In component
 * const showVerifiedBadge = isVerifiedUI(entitlements, userProfile?.is_verified);
 * 
 * @example
 * // For trust score calculation
 * const displayScore = getTrustScoreDisplay(
 *   userProfile?.trust_score,
 *   isVerifiedUI(entitlements, userProfile?.is_verified)
 * );
 */
export function isVerifiedUI(
  entitlements: Entitlements | null | undefined,
  dbIsVerified: boolean | undefined | null
): boolean {
  return getVerificationStatus(entitlements, dbIsVerified).isVerified;
}

/**
 * Normalize peer verified state for Network avatars (connection + cached user row).
 * Does NOT infer verification from trust_score.
 */
export function resolvePeerIsVerified(
  conn?: { isVerified?: boolean; is_verified?: boolean | string | number | null } | null,
  cachedUser?: { is_verified?: boolean | string | number | null } | null,
): boolean {
  if (conn?.isVerified === true) return true;
  const raw = conn?.is_verified ?? cachedUser?.is_verified;
  return raw === true || raw === 'true' || raw === 1;
}

/**
 * V291.2: Debug verification state (DEV only)
 * Logs verification state for debugging trust score display issues
 * 
 * @param entitlements - From EntitlementService
 * @param dbIsVerified - From users.is_verified
 * @param trustScore - Raw trust score from database
 * @param location - Component/location name for debugging
 */
export function logVerificationState(
  entitlements: Entitlements | null | undefined,
  dbIsVerified: boolean | undefined | null,
  trustScore: number | undefined | null,
  location: string
) {
  if (import.meta.env.DEV) {
    const status = getVerificationStatus(entitlements, dbIsVerified);
    
    console.log(`[V291.2 Verification Debug @ ${location}]`, {
      isVerified: status.isVerified,
      source: status.source,
      entitlementsVerified: entitlements?.isVerified,
      dbIsVerified,
      rawTrustScore: trustScore,
      verifiedBonus: status.isVerified ? 100 : 0,
      displayTrustScore: (trustScore ?? 0) + (status.isVerified ? 100 : 0)
    });
  }
}

